import { RateType } from '@treadinc/horizon-api-spec';
import { isNil } from 'lodash-es';
import * as yup from 'yup';

import { commissionPercentageSchema } from '~components/PayCommission/commissionPercentageSchema';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { addOnSchema } from '~pages/Settings/RatesManagement/AutomaticAddOns';
import { getRateTypeOptions } from '~pages/Settings/RatesManagement/rateUtils';

export const rateDetailsSchema = yup.object().shape(
  {
    targetPayCommissionPercentage: commissionPercentageSchema,
    targetRate: yup
      .object()
      .shape({
        id: yup.string().min(1).required(),
        name: yup.string().min(1).required(),
        type: yup.string().min(1).required(),
        rate: yup.number().required(),
        addOns: yup.array().of(addOnSchema),
      })
      .nullable()
      .notRequired(),
    targetRateType: yup
      .object()
      .shape({
        label: yup.string().min(1).required(),
        value: yup.string().min(1).required(),
      })
      .nullable()
      .notRequired()
      .when(['targetRateValue'], {
        is: (targetRateValue: string | number | null | undefined) => {
          if (isNil(targetRateValue)) {
            return false;
          }

          const hasRateValue = String(targetRateValue).trim().length > 0;

          return hasRateValue;
        },
        then: (schema) => schema.nonNullable().required(),
        otherwise: (schema) => schema,
      }),
    targetRateValue: yup
      .number()
      .transform((val) => (Number.isNaN(val) ? null : val))
      .nullable()
      .min(0),
  },
  [['targetRateType', 'targetRateValue']],
);

export type RateDetailsDTO = yup.InferType<typeof rateDetailsSchema>;

export const setRateDetailsFormDefaultValues = (
  targetRate: RateBasicWithValue | null,
  targetRateType: RateType | null,
  targetRateValue: number | null,
) => {
  const selectedRateTypeOption = getRateTypeOptions().find((option) => {
    return option.value === targetRateType;
  });

  const result = {
    targetRate: targetRate?.id
      ? {
          id: targetRate.id,
          name: targetRate.name,
          type: targetRate.type,
          rate: targetRate.rate,
        }
      : null,
    targetRateValue: targetRate?.id ? null : (targetRateValue ?? null),
    targetRateType: targetRate?.id ? null : (selectedRateTypeOption ?? null),
  } as unknown as RateDetailsDTO;

  return result;
};

export interface OrderFormRateFields {
  customerRate?: RateDetailsDTO['targetRate'];
  customerRateType?: RateDetailsDTO['targetRateType'];
  customerRateValue?: RateDetailsDTO['targetRateValue'];
  vendorRate?: RateDetailsDTO['targetRate'];
  vendorRateType?: RateDetailsDTO['targetRateType'];
  vendorRateValue?: RateDetailsDTO['targetRateValue'];
  driverRate?: RateDetailsDTO['targetRate'];
  driverRateType?: RateDetailsDTO['targetRateType'];
  driverRateValue?: RateDetailsDTO['targetRateValue'];
}

type KeyValuePairWithTemplatedKey<Key extends string, Value> = {
  [K in keyof object & string as Key]: Value | null;
};

type DeparsedRateDetailsDTO<T extends string> = KeyValuePairWithTemplatedKey<
  `${T}_rate_id`,
  string
> &
  KeyValuePairWithTemplatedKey<`${T}_rate_type`, RateType> &
  KeyValuePairWithTemplatedKey<`${T}_rate_value`, number>;

type AllowedRateTargets = 'customer' | 'vendor' | 'driver';

const isAllUndefinedRateInput = (input: Partial<RateDetailsDTO>) =>
  input.targetRate === undefined &&
  input.targetRateType === undefined &&
  input.targetRateValue === undefined;

export const deparseRateDetailsDTO = <T extends AllowedRateTargets>(
  prefix: T,
  input: Partial<RateDetailsDTO> | undefined,
) => {
  // Omit the *_rate_* fields when the caller signals "user did not
  // explicitly set this rate" — either as `undefined`, or as a wrapper whose
  // three rate fields are all undefined (the shape produced by the legacy
  // `customerRate: data?.targetRate` pattern in ProjectForm/JobEditForm/etc.).
  // Sending nulls would clobber values applied by an upstream cascade
  // (e.g., Site Rate Schedule).
  if (!input || isAllUndefinedRateInput(input)) {
    return {} as Partial<DeparsedRateDetailsDTO<T>>;
  }

  const rateCardId = input.targetRate?.id ?? null;
  const rateType = input.targetRateType ? (input.targetRateType.value as RateType) : null;
  const rateValue = isNil(input.targetRateValue) ? null : Number(input.targetRateValue);

  const deparsed = {
    ...(rateCardId
      ? {
          [`${prefix}_rate_id`]: rateCardId,
          [`${prefix}_rate_type`]: null,
          [`${prefix}_rate_value`]: null,
        }
      : {
          [`${prefix}_rate_id`]: null,
          [`${prefix}_rate_type`]: rateType,
          [`${prefix}_rate_value`]: rateValue,
        }),
  } as DeparsedRateDetailsDTO<T>;

  return deparsed;
};

export const deparseUpdateRateDetailsDTO = <T extends AllowedRateTargets>(
  target: T,
  input: Partial<RateDetailsDTO> | undefined,
) => {
  // See deparseRateDetailsDTO above for the omission rationale.
  if (!input || isAllUndefinedRateInput(input)) {
    return {} as Partial<DeparsedRateDetailsDTO<T>>;
  }

  const rateCardId = input.targetRate?.id ?? null;
  const rateType = input.targetRateType ? (input.targetRateType.value as RateType) : null;
  const rateValue = isNil(input.targetRateValue) ? null : Number(input.targetRateValue);

  const deparsed = {
    [`${target}_rate_id`]: rateCardId,
    [`${target}_rate_type`]: rateType,
    [`${target}_rate_value`]: rateValue,
  } as DeparsedRateDetailsDTO<T>;

  return deparsed;
};
